// #region Imports
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import TeamAvatar from "marketplace/_views/avatars/team-avatar";
import {
  deleteTeamBookmark,
  getErrorLoadingTeamBookmarks,
  getIsLoadingTeamBookmarks,
  getTeamBookmarks,
  populateTeamBookmarksForUser,
} from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import { PageTab } from "marketplace/bookmarks/_views/bookmarks-page-tabs";
import BookmarkedTeam from "marketplace/bookmarks/team-bookmarks/bookmarked-team";
import ViewTeamProfile from "marketplace/team-profile/_views/view-team-profile";
import {
  getTeamsByIds,
  populateMarketplaceTeamsByIds,
} from "marketplace/teams/_store/marketplace-teams-redux-slice";
import Team from "marketplace/teams/team";
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSession } from "users/__session/session-context";
// #endregion

// #region Styled Components
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
// #endregion

// #region Component Input Props
type TeamBookmarksProps = {
  activeTab: PageTab;
};

const columnHelper = createMRTColumnHelper<BookmarkedTeam>();

export default function TeamBookmarks(props: Readonly<TeamBookmarksProps>) {
  const { activeTab } = props;

  // #region Hooks
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const bookmarks = useSelector(getTeamBookmarks());
  const bookmarksLoading = useSelector(getIsLoadingTeamBookmarks());
  const errorLoadingBookmarks = useSelector(getErrorLoadingTeamBookmarks());

  // All teamIds to fetch
  const bookmarkedTeamIds = useMemo(
    () => bookmarks?.map(b => b.teamId) ?? [],
    [bookmarks]
  );

  // Team Selectors
  const teams = useSelector(getTeamsByIds(bookmarkedTeamIds));
  const teamEntries = useSelector((s: RootState) => s.teams.byId.entries);
  const loadingById = useSelector((s: RootState) => s.teams.byId.loading);
  const errorById = useSelector((s: RootState) => s.teams.byId.error);

  const teamsById = useMemo(() => {
    const map: Record<string, Team> = {};
    for (const team of teams) {
      map[team.id.value] = team;
    }
    return map;
  }, [teams]);

  // #region Lifecycle Hooks
  useEffect(() => {
    if (!bookmarkedTeamIds.length) return;

    const missingIds = bookmarkedTeamIds.filter((id) => {
      if (teamEntries[id.value]) return false;
      if (loadingById[id.value]) return false;
      return true;
    });

    if (!missingIds.length) return;

    dispatch(populateMarketplaceTeamsByIds({ session, ids: missingIds }));
  }, [
    dispatch,
    session,
    bookmarkedTeamIds,
    teamEntries,
    loadingById
  ]);

  useEffect(() => {
    if ((bookmarks?.length ?? 0 ) > 0) return;
    dispatch(populateTeamBookmarksForUser({ session }));
  }, [bookmarks?.length, dispatch, session]);

  useEffect(() => {
    if (errorLoadingBookmarks) {
      enqueueSnackbar("Error loading bookmarks. Please try again.", { variant: 'error' });
    }
  }, [errorLoadingBookmarks]);

  // #region Event Handlers
  async function handleDeleteBookmark(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    bookmark: BookmarkedTeam
  ) {
    event.stopPropagation();

    const response = await confirm({
      title: "Delete Bookmark?",
      message: "Do you want to delete?",
      okButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await dispatch(deleteTeamBookmark({ 
        bookmarkId: bookmark.id ,
        session
      })).unwrap();
      enqueueSnackbar("Deleted bookmark", { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't delete bookmark. Please try again", { variant: "error" });
    }
  }

  function handleViewProfile(profileId?: Guid): void {
    if (!profileId) return;
    openDialog({
      component: <ViewTeamProfile teamId={profileId} />,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: false,
      },
    });
  }
  // #endregion

  // #region Columns
  const renderNameColumn = useCallback((bookmark: BookmarkedTeam) => {
    const id = bookmark.teamId;
    const team = teamsById[id.value];

    const isLoading = !!loadingById[id.value];
    const hasError = !!errorById[id.value];

    const loadingText = isLoading ? "Loading..." : "Unknown Team";

    const name =
      team?.profile?.name ??
      (hasError ? "Error loading" : loadingText);

    return (
      <NameCellContainer>
        <TeamAvatar team={team ?? null} session={session} />
        <RowTitle>{name}</RowTitle>
      </NameCellContainer>
    );
  }, [teamsById, loadingById, errorById, session]);

  const columns = useMemo(() => [
    columnHelper.display({
      header: "Name",
      id: "name",
      Cell: ({ row }) => renderNameColumn(row.original),
    }),
    columnHelper.accessor((bookmark) => bookmark.createdDate.format("MM/DD/YY"), {
      header: "Bookmarked On",
      id: "created",
    }),
  ], [renderNameColumn]);
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<BookmarkedTeam> = {
    columns,
    data: bookmarks ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    enableColumnPinning: true,
    enableRowActions: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleViewProfile(row.original.teamId),
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Bookmarks</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => navigate(`/marketplace/bookmarks/${newValue}`)}
            >
              <Tab value={PageTab.Individual} label="Individual" />
              <Tab value={PageTab.Team} label="Team" />
              <Tab value={PageTab.Company} label="Company" />
            </Tabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    renderRowActions: ({ row }) => (
      <Tooltip title="Delete Bookmark">
        <IconButton onClick={(event) => handleDeleteBookmark(event, row.original)}>
          <DeleteIcon color="error" />
        </IconButton>
      </Tooltip>
    ),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps:
      errorLoadingBookmarks === null
        ? undefined
        : {
          color: "error",
          children: "Error loading data",
        },
    // onColumnFiltersChange: setColumnFilters,
    // onGlobalFilterChange: setGlobalFilter,
    // onPaginationChange: setPagination,
    // onSortingChange: setSorting,
    rowCount: bookmarks?.length ?? 0,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading: bookmarksLoading,
      // pagination,
      showAlertBanner: errorLoadingBookmarks !== null
        || bookmarkedTeamIds?.some((teamId) => !!errorById?.[teamId.value]),
      showProgressBars: bookmarksLoading,
      // sorting,
    },
  }

  return <MRTNoMemo<BookmarkedTeam> options={tableOptions} />;
}
